import { DateTime, Duration } from "luxon";
import { getTimezoneName } from "./locale";

/**
 * Parses a UTC datetime string that may be in ISO 8601 or SQL ("yyyy-MM-dd HH:mm:ss") format.
 */
function parseUtcDateTime(value: string): DateTime {
    const iso = DateTime.fromISO(value, { zone: "UTC" });
    if (iso.isValid) {
        return iso;
    }
    return DateTime.fromSQL(value, { zone: "UTC" });
}

export function convertToTimezoneLocalized(utcDateTime: string, targetTimezone: string, format: string): string {
    if (!utcDateTime) {
        return "";
    }
    return parseUtcDateTime(utcDateTime).setZone(targetTimezone).setLocale(window.panelLocale).toFormat(format);
}

export function convertToTimezone(utcDateTime: string, targetTimezone: string, format: string = "yyyy-MM-dd HH:mm:ss") {
    if (!utcDateTime) {
        return "";
    }

    return parseUtcDateTime(utcDateTime).setZone(targetTimezone).toFormat(format);
}

export function convertFromTimezone(sourceDateTime: string | null | undefined, sourceTimezone: string) {
    if (!sourceDateTime) {
        return "";
    }

    return DateTime.fromSQL(sourceDateTime, { zone: sourceTimezone }).toUTC().toFormat("yyyy-MM-dd HH:mm:ss");
}

/**
 * Automatically deduces time zone of user and gives Y-m-d H:i:s date.
 */
export function autoConvertToTimezone(utcDateTime: string, format: string = "yyyy-MM-dd HH:mm:ss") {
    const timezone = getUserTimezone();

    return convertToTimezone(utcDateTime, timezone, format);
}

/**
 * Like autoConvertToTimezone but formats with the user's locale (window.panelLocale),
 * so month names etc. are rendered in the correct language.
 */
export function autoConvertToTimezoneLocalized(utcDateTime: string, format: string = "yyyy-MM-dd HH:mm:ss") {
    const timezone = getUserTimezone();

    return convertToTimezoneLocalized(utcDateTime, timezone, format);
}

export function convertToTimezoneHourMinutes(utcHourMinutes, baseDate, targetTimezone) {
    if (!utcHourMinutes) {
        return "";
    }

    const sourceDateTime = `${baseDate} ${utcHourMinutes}:00`;
    return DateTime.fromSQL(sourceDateTime, { zone: "UTC" }).setZone(targetTimezone).toFormat("HH:mm");
}

export function getUserTimezone() {
    return getTimezoneName();
}

export function getDateFromDateTime(dateTime) {
    const [date] = dateTime.split(" ");

    return date;
}

/**
 * Formats a calendar date ("yyyy-MM-dd", e.g. a birth date) in the user's locale as-is, without any timezone shift.
 */
export const formatCalendarDate = (date: string): string => {
    return DateTime.fromISO(date, { zone: "UTC" }).setLocale(window.panelLocale).toLocaleString(DateTime.DATE_SHORT);
};

export const fromNow = (dateTime: string | number) => {
    const dt = typeof dateTime === "number" ? DateTime.fromMillis(dateTime) : DateTime.fromSQL(dateTime);
    return dt.toRelative({ locale: window.panelLocale }) ?? "";
};

export const dateTimesToDaysRange = (startDateTime: string, endDateTime: string, timezone: string) => {
    const locale = window.panelLocale;
    const startDate = parseUtcDateTime(startDateTime).setZone(timezone).setLocale(locale);
    const endDate = parseUtcDateTime(endDateTime).setZone(timezone).setLocale(locale);

    if (endDate.diff(startDate, "hours").hours > 24) {
        return `${startDate.toLocaleString(DateTime.DATE_SHORT)} - ${endDate.toLocaleString(DateTime.DATE_SHORT)}`;
    } else {
        return startDate.toLocaleString(DateTime.DATE_FULL);
    }
};

/**
 * Returns current DateTime object in the specified timezone
 */
export const getCurrentDateTimeInTimezone = (timezone: string): DateTime => {
    return DateTime.now().setZone(timezone);
};

/**
 * Parses a datetime string in a specific timezone and returns DateTime object
 */
export const parseDateTimeInTimezone = (dateTime: string, format: string, timezone: string): DateTime => {
    return DateTime.fromFormat(dateTime, format, { zone: timezone });
};

/**
 * Parses a UTC datetime string (ISO or SQL format) and converts it to the given timezone, returning a DateTime object
 */
export const parseUtcDateTimeInTimezone = (utcDateTime: string, timezone: string): DateTime => {
    return parseUtcDateTime(utcDateTime).setZone(timezone);
};

/**
 * Calculates the difference in days between two DateTime objects
 */
export const calculateDaysDifference = (laterDateTime: DateTime, earlierDateTime: DateTime): number => {
    return laterDateTime.diff(earlierDateTime, "days").days;
};

/**
 * Adds days to current time in timezone and returns formatted string
 */
export const addDaysToCurrentTime = (days: number, timezone: string, format: string = "yyyy-MM-dd HH:mm:ss"): string => {
    return DateTime.now().setZone(timezone).plus({ days }).toFormat(format);
};

/**
 * Returns true if the current time is between two UTC datetime strings (inclusive).
 */
export const isNowBetweenUtc = (startDate: string, endDate: string): boolean => {
    const now = DateTime.now().toMillis();
    const start = parseUtcDateTime(startDate).toMillis();
    const end = parseUtcDateTime(endDate).toMillis();
    return now >= start && now <= end;
};

/**
 * Returns true if the given UTC datetime string is before now.
 */
export const isUtcPast = (date: string): boolean => {
    return parseUtcDateTime(date).toMillis() < DateTime.now().toMillis();
};

/**
 * Humanizes the duration from now to a UTC datetime string, with a directional suffix.
 */
export const humanizeUtcDiffFromNow = (date: string): string => {
    return parseUtcDateTime(date).toRelative({ locale: window.panelLocale }) ?? "";
};

/**
 * Formats a date range label from start and optional end date strings.
 * Returns a single date if start and end are the same day or end is absent.
 * When timezone is provided, UTC datetime strings are converted to that timezone before comparison.
 */
export const formatDateRange = (startDate: string, endDate?: string | null, timezone?: string): string => {
    const locale = window.panelLocale;
    const parse = (date: string) =>
        timezone ? parseUtcDateTime(date).setZone(timezone).setLocale(locale) : DateTime.fromSQL(date).setLocale(locale);

    const start = parse(startDate);

    if (!endDate) {
        return start.toLocaleString(DateTime.DATE_FULL);
    }

    const end = parse(endDate);
    if (start.hasSame(end, "day")) {
        return start.toLocaleString(DateTime.DATE_FULL);
    }

    return `${start.toFormat("d MMM")} – ${end.toLocaleString(DateTime.DATE_FULL)}`;
};

/**
 * Converts a UTC datetime string to the given timezone and formats it as a locale-aware medium datetime string.
 */
export const convertToTimezoneDateTimeMed = (utcDateTime: string, timezone: string): string => {
    const locale = window.panelLocale;
    return parseUtcDateTime(utcDateTime).setZone(timezone).setLocale(locale).toLocaleString(DateTime.DATETIME_MED);
};

/**
 * Calculates the number of seconds elapsed from a UTC datetime string to the current UTC time
 * @param utcDateTime - UTC datetime string in "yyyy-MM-dd HH:mm:ss" format
 * @returns Number of seconds elapsed (can be negative if the datetime is in the future)
 */
export const getElapsedSecondsFromUtc = (utcDateTime: string): number => {
    return DateTime.now().toUTC().diff(parseUtcDateTime(utcDateTime), "seconds").seconds;
};

/**
 * Adds N days to a Date and returns a new Date.
 */
export const addDays = (date: Date, days: number): Date => new Date(date.getTime() + days * 86400000);

/**
 * Returns the difference in whole days between two dates (a - b).
 */
export const diffInDays = (a: Date, b: Date): number => Math.round((a.getTime() - b.getTime()) / 86400000);

/**
 * Humanizes a duration (e.g., "5 minutes", "in 2 hours")
 * @param value - The numeric value of the duration
 * @param unit - The unit of time (e.g., "minutes", "hours", "days")
 * @param locale - The locale to use for formatting
 * @param withSuffix - If true, adds prefix/suffix like "in" or "ago" (default: false)
 * @returns Humanized duration string in the specified locale
 */
export const humanizeDuration = (value: number, unit: string, locale: string, withSuffix: boolean = false): string => {
    if (withSuffix) {
        const now = DateTime.now();
        return now.plus({ [unit]: value }).toRelative({ locale, base: now }) ?? "";
    }
    return Duration.fromObject({ [unit]: value }, { locale }).toHuman();
};
